<template>
  <div class="space-y-6">
    <section
      v-if="hasQualifications"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        Qualifications
      </h2>
      <div class="space-y-4">
        <QualificationItem
          v-if="analysis.qualifications.education_required"
          icon="i-heroicons-book-open"
          label="Education"
          :value="analysis.qualifications.education_required"
        />
        <QualificationItem
          v-if="analysis.qualifications.licenses_required?.length"
          icon="i-heroicons-identification"
          label="Required Licenses"
          :items="analysis.qualifications.licenses_required"
        />
        <QualificationItem
          v-if="analysis.qualifications.certifications_required?.length"
          icon="i-heroicons-shield-check"
          label="Required Certifications"
          :items="analysis.qualifications.certifications_required"
        />
        <QualificationItem
          v-if="analysis.qualifications.certifications_preferred?.length"
          icon="i-heroicons-sparkles"
          label="Preferred Certifications"
          :items="analysis.qualifications.certifications_preferred"
          :preferred="true"
        />
      </div>
    </section>
    <section
      v-if="analysis.responsibilities?.primary_responsibilities?.length"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        Responsibilities
      </h2>
      <div class="space-y-4">
        <div v-if="analysis.responsibilities.primary_responsibilities?.length">
          <h3 class="font-semibold text-gray-900 mb-3">
            Primary Duties
          </h3>
          <ul class="space-y-2 list-disc list-inside">
            <li
              v-for="(responsibility, index) in analysis.responsibilities.primary_responsibilities"
              :key="index"
              class="text-gray-700"
            >
              {{ responsibility }}
            </li>
          </ul>
        </div>
        <div
          v-if="analysis.responsibilities.secondary_responsibilities?.length"
          class="pt-4 border-t border-gray-200"
        >
          <h3 class="font-semibold text-gray-900 mb-3">
            Additional Duties
          </h3>
          <ul class="space-y-2 list-disc list-inside">
            <li
              v-for="(responsibility, index) in analysis.responsibilities.secondary_responsibilities"
              :key="index"
              class="text-gray-700"
            >
              {{ responsibility }}
            </li>
          </ul>
        </div>
      </div>
    </section>
    <section
      v-if="analysis.job_summary?.overview || analysis.job_summary?.role_objectives?.length"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        About This Role
      </h2>
      <p
        v-if="analysis.job_summary.overview"
        class="text-gray-700 leading-relaxed mb-4"
      >
        {{ analysis.job_summary.overview }}
      </p>
      <div
        v-if="analysis.job_summary.role_objectives?.length"
        class="mt-4"
      >
        <h3 class="font-semibold text-gray-900 mb-3">
          Key Objectives
        </h3>
        <ol class="space-y-2 list-decimal list-inside">
          <li
            v-for="(objective, index) in analysis.job_summary.role_objectives"
            :key="index"
            class="text-gray-700"
          >
            {{ objective }}
          </li>
        </ol>
      </div>
    </section>
    <section
      v-if="analysis.experience?.experience_required || analysis.experience?.experience_preferred"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        Experience Requirements
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-if="analysis.experience.experience_required"
          class="space-y-2"
        >
          <h3 class="font-semibold text-gray-900">
            Required
          </h3>
          <p class="text-gray-700">
            {{ analysis.experience.experience_required }}
          </p>
        </div>
        <div
          v-if="analysis.experience.experience_preferred"
          class="space-y-2"
        >
          <h3 class="font-semibold text-gray-900">
            Preferred
          </h3>
          <p class="text-gray-700">
            {{ analysis.experience.experience_preferred }}
          </p>
        </div>
      </div>
      <div
        v-if="analysis.experience.experience_years"
        class="mt-4 pt-4 border-t border-gray-200"
      >
        <p class="text-gray-700 font-medium">
          {{ analysis.experience.experience_years }} year{{ analysis.experience.experience_years !== 1 ? 's' : '' }} of
          experience
        </p>
      </div>
    </section>
    <section
      v-if="hasBenefits"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        Benefits & Perks
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <BenefitCategory
          v-if="analysis.compensation_and_benefits.benefits?.length"
          title="Benefits Package"
          :items="analysis.compensation_and_benefits.benefits"
          icon="i-heroicons-check-badge"
        />
        <BenefitCategory
          v-if="analysis.compensation_and_benefits.insurance_coverage?.length"
          title="Insurance Coverage"
          :items="analysis.compensation_and_benefits.insurance_coverage"
          icon="i-heroicons-shield-check"
        />
        <BenefitCategory
          v-if="analysis.compensation_and_benefits.retirement_plan"
          title="Retirement"
          :items="[analysis.compensation_and_benefits.retirement_plan]"
          icon="i-heroicons-banknotes"
        />
        <BenefitCategory
          v-if="analysis.compensation_and_benefits.vacation_policy"
          title="Time Off"
          :items="[analysis.compensation_and_benefits.vacation_policy]"
          icon="i-heroicons-calendar"
        />
      </div>
    </section>
    <section
      v-if="hasSkills"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        Required Skills
      </h2>
      <div class="space-y-4">
        <SkillCategory
          v-if="analysis.skills.technical_skills?.length"
          title="Technical Skills"
          :skills="analysis.skills.technical_skills"
          color="blue"
        />
        <SkillCategory
          v-if="analysis.skills.soft_skills?.length"
          title="Soft Skills"
          :skills="analysis.skills.soft_skills"
          color="purple"
        />
        <SkillCategory
          v-if="analysis.skills.leadership_skills?.length"
          title="Leadership"
          :skills="analysis.skills.leadership_skills"
          color="green"
        />
      </div>
    </section>
    <section
      v-if="jobDescription"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        Full Job Description
      </h2>
      <div
        v-if="hasHTMLFormatting"
        class="prose prose-sm max-w-none text-gray-700"
        v-html="jobDescription"
      />
      <div
        v-else
        class="text-gray-700 whitespace-pre-wrap leading-relaxed"
      >
        {{ jobDescription }}
      </div>
    </section>
    <section
      v-if="hasCulture"
      class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
    >
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        Company Culture
      </h2>
      <div class="space-y-4">
        <div v-if="analysis.cultural_and_ethical_attributes.core_values?.length">
          <h3 class="font-semibold text-gray-900 mb-2">
            Core Values
          </h3>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(value, index) in analysis.cultural_and_ethical_attributes.core_values"
              :key="index"
              class="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium"
            >
              {{ value }}
            </span>
          </div>
        </div>
        <p
          v-if="analysis.cultural_and_ethical_attributes.work_environment"
          class="text-gray-700"
        >
          <strong>Work Environment:</strong> {{ analysis.cultural_and_ethical_attributes.work_environment }}
        </p>
        <p
          v-if="analysis.cultural_and_ethical_attributes.community_engagement"
          class="text-gray-700"
        >
          <strong>Community Engagement:</strong> {{ analysis.cultural_and_ethical_attributes.community_engagement }}
        </p>
      </div>
    </section>
    <section class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      <h2 class="text-lg font-bold text-gray-900 mb-4">
        How to Apply
      </h2>
      <div class="space-y-4">
        <p
          v-if="hasApplicationInfo && analysis.application_process.application_instructions"
          class="text-gray-700"
        >
          {{ analysis.application_process.application_instructions }}
        </p>
        <div
          v-if="hasApplicationInfo && analysis.application_process.required_documents?.length"
          class="pt-3 border-t border-gray-200"
        >
          <h3 class="font-semibold text-gray-900 mb-2">
            Required Documents:
          </h3>
          <ul class="space-y-1 list-disc list-inside">
            <li
              v-for="(doc, index) in analysis.application_process.required_documents"
              :key="index"
              class="text-gray-700"
            >
              {{ doc }}
            </li>
          </ul>
        </div>
        <div class="hidden sm:flex justify-end pt-4">
          <Button
            size="md"
            color="black"
            variant="solid"
            class="text-white"
            @click="$emit('apply')"
          >
            Apply
          </Button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import QualificationItem from './analysis/QualificationItem.vue'
import SkillCategory from './analysis/SkillCategory.vue'
import BenefitCategory from './analysis/BenefitCategory.vue'

const props = defineProps<{
  analysis: any
}>()
const jobDescription = computed(() => {
  return props.analysis.reformatted_description || props.analysis.original_description || ''
})
const hasHTMLFormatting = computed(() => {
  if (!jobDescription.value) return false
  const htmlTagPattern = /<(strong|br|ul|li|p|div|span|em|b|i|h[1-6])[^>]*>/i
  return htmlTagPattern.test(jobDescription.value)
})
const hasQualifications = computed(() => {
  if (!props.analysis.qualifications) return false
  return !!(
    (props.analysis.qualifications.education_required && props.analysis.qualifications.education_required.trim())
    || (props.analysis.qualifications.licenses_required?.length && props.analysis.qualifications.licenses_required.length > 0)
    || (props.analysis.qualifications.certifications_required?.length && props.analysis.qualifications.certifications_required.length > 0)
    || (props.analysis.qualifications.certifications_preferred?.length && props.analysis.qualifications.certifications_preferred.length > 0)
  )
})
const hasSkills = computed(() => {
  if (!props.analysis.skills) return false
  return !!(
    (props.analysis.skills.technical_skills?.length && props.analysis.skills.technical_skills.length > 0)
    || (props.analysis.skills.soft_skills?.length && props.analysis.skills.soft_skills.length > 0)
    || (props.analysis.skills.leadership_skills?.length && props.analysis.skills.leadership_skills.length > 0)
  )
})
const hasBenefits = computed(() => {
  if (!props.analysis.compensation_and_benefits) return false
  return !!(
    (props.analysis.compensation_and_benefits.benefits?.length && props.analysis.compensation_and_benefits.benefits.length > 0)
    || (props.analysis.compensation_and_benefits.insurance_coverage?.length && props.analysis.compensation_and_benefits.insurance_coverage.length > 0)
    || (props.analysis.compensation_and_benefits.retirement_plan && props.analysis.compensation_and_benefits.retirement_plan.trim())
    || (props.analysis.compensation_and_benefits.vacation_policy && props.analysis.compensation_and_benefits.vacation_policy.trim())
  )
})
const hasCulture = computed(() => {
  if (!props.analysis.cultural_and_ethical_attributes) return false
  return !!(
    (props.analysis.cultural_and_ethical_attributes.core_values?.length && props.analysis.cultural_and_ethical_attributes.core_values.length > 0)
    || (props.analysis.cultural_and_ethical_attributes.work_environment && props.analysis.cultural_and_ethical_attributes.work_environment.trim())
    || (props.analysis.cultural_and_ethical_attributes.community_engagement && props.analysis.cultural_and_ethical_attributes.community_engagement.trim())
  )
})
const hasApplicationInfo = computed(() => {
  return props.analysis.application_process?.application_instructions
    || props.analysis.application_process?.required_documents?.length
})
</script>
